import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/modern/utils";

const cardVariants = cva(" border", {
  variants: {
    variant: {
      default: "bg-card text-card-foreground",
      primary: "border-primary bg-primary text-primary-foreground",
      "primary-outline": "border-primary bg-primary/10 text-primary",
      success: "border-success bg-success text-success-foreground",
      "success-outline": "border-success bg-success/10 text-success",
      outline: "border border-border text-foreground bg-transparent",
      modern: "bg-card shadow-none",
      ghost: "bg-transparent hover:bg-accent border-none shadow-none transition-colors",
      transparent: "bg-transparent border-none shadow-none"
    },
    size: {
      default: "",
      sm: "p-2",
      base: ""
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, size, ...props }, ref) => <div ref={ref} className={cn(cardVariants({ variant, size }), className)} {...props} />);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-2xl font-normal leading-none tracking-tight", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => <div ref={ref} className={cn(" pt-0", className)} {...props} />);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center  pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
